import { useMemo, useState } from 'react';
import { Anchor, Button, Group, NumberInput, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useIntl } from 'react-intl';

import { wizardInsuranceFooterMessages } from './wizard-insurance-footer-messages';
import styles from './WizardInsuranceFooter.module.css';
import { Dropdown } from '../../../common/Dropdown';
import { freightFormMessages } from '../../../freight-browser/components/freight-form/freight-form-messages';

import type { InsuranceCommodityCategory, InsuranceItemCondition } from '../../../../types/freight-requests';
import type { WizardInsurance } from '../../freight-booking-wizard-types';

type WizardInsuranceFooterProps = {
  insurance: WizardInsurance;
  onInsuranceChange: (next: WizardInsurance) => void;
};

const CONDITION_VALUES: InsuranceItemCondition[] = ['New', 'Used'];

const CATEGORY_VALUES: InsuranceCommodityCategory[] = [
  'GeneralMerchandise',
  'Equipment',
  'FoodBeverages',
  'ChemicalsPaint',
  'Electronics',
  'Construction',
  'Furniture',
  'Stonework',
  'FragileItems',
  'Perishables',
  'Jewelry',
  'MotorizedTransport',
  'ArtAntiques',
  'Explosives',
  'LifeForms',
  'ControlledItems',
  'PharmaNonRefrigerated',
  'PharmaRefrigerated',
];

const CONDITION_MESSAGE_MAP: Record<InsuranceItemCondition, keyof typeof freightFormMessages> = {
  New: 'conditionNew',
  Used: 'conditionUsed',
};

const CATEGORY_MESSAGE_MAP: Record<InsuranceCommodityCategory, keyof typeof freightFormMessages> = {
  GeneralMerchandise: 'categoryGeneralMerchandise',
  Equipment: 'categoryEquipment',
  FoodBeverages: 'categoryFoodBeverages',
  ChemicalsPaint: 'categoryChemicalsPaint',
  Electronics: 'categoryElectronics',
  Construction: 'categoryConstruction',
  Furniture: 'categoryFurniture',
  Stonework: 'categoryStonework',
  FragileItems: 'categoryFragileItems',
  Perishables: 'categoryPerishables',
  Jewelry: 'categoryJewelry',
  MotorizedTransport: 'categoryMotorizedTransport',
  ArtAntiques: 'categoryArtAntiques',
  Explosives: 'categoryExplosives',
  LifeForms: 'categoryLifeForms',
  ControlledItems: 'categoryControlledItems',
  PharmaNonRefrigerated: 'categoryPharmaNonRefrigerated',
  PharmaRefrigerated: 'categoryPharmaRefrigerated',
};

export const WizardInsuranceFooter = ({ insurance, onInsuranceChange }: WizardInsuranceFooterProps) => {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);

  const conditionOptions = useMemo(
    () => CONDITION_VALUES.map((v) => ({ value: v, label: intl.formatMessage(freightFormMessages[CONDITION_MESSAGE_MAP[v]]) })),
    [intl],
  );

  const categoryOptions = useMemo(
    () => CATEGORY_VALUES.map((v) => ({ value: v, label: intl.formatMessage(freightFormMessages[CATEGORY_MESSAGE_MAP[v]]) })),
    [intl],
  );

  const handleToggle = (checked: boolean) => {
    onInsuranceChange({ ...insurance, isInsured: checked });
    if (checked) setIsEditing(true);
    else setIsEditing(false);
  };

  const switchRow = (
    <Group justify='space-between' wrap='nowrap'>
      <Stack gap={2}>
        <Switch
          checked={insurance.isInsured}
          classNames={{ label: styles.switchLabel }}
          data-testid='insurance-toggle'
          label={intl.formatMessage(wizardInsuranceFooterMessages.insureThisShipment)}
          onChange={(e) => handleToggle(e.currentTarget.checked)}
        />
        <Anchor
          // TODO(link): real UPS Capital T&C url
          c='var(--adl-button-primary-hyperlink-text)'
          href='#'
          rel='noopener noreferrer'
          size='sm'
          target='_blank'
        >
          {intl.formatMessage(wizardInsuranceFooterMessages.upsCapitalTerms)}
        </Anchor>
      </Stack>
      {insurance.isInsured && !isEditing && (
        <Button data-testid='insurance-edit' onClick={() => setIsEditing(true)} variant='ghost'>
          {intl.formatMessage(wizardInsuranceFooterMessages.edit)}
        </Button>
      )}
    </Group>
  );

  if (!insurance.isInsured) {
    return (
      <div className={styles.insuranceSection} data-testid='wizard-insurance-footer'>
        {switchRow}
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className={styles.insuranceSection} data-testid='wizard-insurance-footer'>
        <Stack gap='sm'>
          {switchRow}
          <Group grow>
            <Dropdown
              data={conditionOptions}
              data-testid='insurance-condition'
              label={intl.formatMessage(freightFormMessages.conditionOfGoodsLabel)}
              onChange={(val) => onInsuranceChange({ ...insurance, conditionOfGoods: val ?? '' })}
              placeholder={intl.formatMessage(freightFormMessages.selectOnePlaceholder)}
              value={insurance.conditionOfGoods || null}
            />
            <Dropdown
              data={categoryOptions}
              data-testid='insurance-category'
              label={intl.formatMessage(freightFormMessages.categoryLabel)}
              onChange={(val) => onInsuranceChange({ ...insurance, category: val ?? '' })}
              placeholder={intl.formatMessage(freightFormMessages.selectOnePlaceholder)}
              value={insurance.category || null}
            />
          </Group>
          <Group grow>
            <NumberInput
              allowNegative={false}
              data-testid='insurance-value'
              label={intl.formatMessage(freightFormMessages.insuredValueLabel)}
              min={0}
              onChange={(val) => onInsuranceChange({ ...insurance, insuredValue: typeof val === 'string' ? parseFloat(val) || 0 : val })}
              prefix='$ '
              value={insurance.insuredValue}
            />
            <TextInput
              data-testid='insurance-marks'
              label={intl.formatMessage(freightFormMessages.marksAndNumbersLabel)}
              onChange={(e) => onInsuranceChange({ ...insurance, marksAndNumbers: e.currentTarget.value })}
              placeholder={intl.formatMessage(wizardInsuranceFooterMessages.marksAndNumbersPlaceholder)}
              value={insurance.marksAndNumbers}
            />
          </Group>
          <Button className={styles.doneButton} data-testid='insurance-done' onClick={() => setIsEditing(false)} variant='ghost'>
            {intl.formatMessage(wizardInsuranceFooterMessages.done)}
          </Button>
        </Stack>
      </div>
    );
  }

  // isInsured && !isEditing — summary view
  const conditionLabel = conditionOptions.find((o) => o.value === insurance.conditionOfGoods)?.label ?? '—';
  const categoryLabel = categoryOptions.find((o) => o.value === insurance.category)?.label ?? '—';

  return (
    <div className={styles.insuranceSection} data-testid='wizard-insurance-footer'>
      <Stack gap={0}>
        {switchRow}
        <div className={styles.summaryRow}>
          <SummaryMeasurement label={intl.formatMessage(wizardInsuranceFooterMessages.summaryConditionLabel)} value={conditionLabel} />
          <SummaryMeasurement label={intl.formatMessage(wizardInsuranceFooterMessages.summaryCategoryLabel)} value={categoryLabel} />
          <SummaryMeasurement
            label={intl.formatMessage(wizardInsuranceFooterMessages.summaryInsuredValueLabel)}
            value={insurance.insuredValue ? intl.formatNumber(insurance.insuredValue, { style: 'currency', currency: 'USD' }) : '—'}
          />
          <SummaryMeasurement
            label={intl.formatMessage(wizardInsuranceFooterMessages.summaryMarksLabel)}
            value={insurance.marksAndNumbers || '—'}
          />
        </div>
      </Stack>
    </div>
  );
};

type SummaryMeasurementProps = {
  label: string;
  value: string;
};

const SummaryMeasurement = ({ label, value }: SummaryMeasurementProps) => (
  <Stack gap={2}>
    <Text c='var(--adl-text-secondary)' size='xs'>
      {label}
    </Text>
    <Text fw={600} size='sm'>
      {value}
    </Text>
  </Stack>
);
